import React, { useEffect, useState } from "react";
import useProjectTimeline from "../hooks/useProjectTimeline";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import { AppToolbar } from "../components/AppToolbar";
import { SpinnerWrapper } from "../components/SpinnerWrapper";
import useTableHeight from "../hooks/useTableHeight";
import ProjectFilters from "../components/ProjectFilters";
import { useTranslation } from "react-i18next";
import { ProjectTimelinePosition } from "../types/ProjectTimelineTypes";
import TimelineComponent, { TimelineGroup, TimelineItem } from "../components/TimelineComponent";
import OpsDateUtils from "../utils/OpsDateUtils";
import AssignationEditDialog from "../components/EditionDialogs/AssignationEditDialog";
import PositionFormDialog from "../components/EditionDialogs/PositionFormDialog";
import { FILTER_FUNCTIONS, PositionStatusFilters } from "../utils/enums/filters/PositionStatusFilters";
import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";

function ProjectTimelineView() {
  const { t } = useTranslation();

  const {
    userIsManager,
    positionNames,
    projectsNames,
    handleSelectProject,
    isLoading,
    selectedProject,
    rows,
    onAssignationUpdate,
    fetchPositions,
    loadUserNames,
    positionStatusFilter,
    setPositionStatusFilter,
  } = useProjectTimeline();

  const { bottomSectionRef } = useTableHeight();
  const [groups, setGroups] = useState<TimelineGroup[]>([]);
  const [items, setItems] = useState<TimelineItem[]>([]);
  const [startDate, setStartDate] = useState(new Date());
  const [assignationExternalId, setAssignationExternalId] = useState<string | undefined>();
  const [editMode, setEditMode] = useState(true);
  const [userNames, setUserNames] = useState<AutoCompleteUIDTO[] | undefined>();
  const [positionExternalId, setPositionExternalId] = useState<string | undefined>();

  useEffect(() => {
    const items = getAllItems(true);
    setItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, groups]);

  useEffect(() => {
    const filteredGroups = filterGroups();
    setGroups(filteredGroups);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionNames, positionStatusFilter]);

  const getAllGroups = () => {
    return positionNames.map((position: ProjectTimelinePosition) => ({
      id: position.externalId,
      title: position.externalName,
      start_time: position.startDate,
      end_time: position.endDate,
    }));
  };

  const getAllItems = (includeGroups: boolean = false) => {
    const timelineItems = rows.map(({ assignationExternalId, positionExternalId, coreUserName, startDate, endDate }) => ({
      id: assignationExternalId,
      group: positionExternalId,
      title: `${coreUserName} ${!endDate ? " (no end time)" : ""}`,
      start_time: new Date(startDate),
      end_time: endDate ? new Date(endDate) : OpsDateUtils.addMonthsToDate(startDate, 12),
      canMove: false,
      canResize: false,
      isGroup: false,
      itemProps: {},
    }));
    if (!includeGroups) {
      return timelineItems;
    }

    const groupItems = groups.map(({ id, start_time, end_time }) => ({
      id,
      group: id,
      title: "",
      start_time,
      end_time: end_time ? new Date(end_time) : OpsDateUtils.addMonthsToDate(start_time, 12),
      canMove: false,
      canResize: false,
      isGroup: true,
      itemProps: {
        className: "group-item",
      },
    }));

    return timelineItems.concat(groupItems);
  };

  const hasAssignations = (group: TimelineGroup): boolean => {
    return items.some((item) => item.group === group.id);
  };

  const filterGroups = (): TimelineGroup[] => {
    const groups = getAllGroups();
    const status = positionStatusFilter as PositionStatusFilters;
    return groups.filter((group) => FILTER_FUNCTIONS[status](group, hasAssignations(group)));
  };

  const onChangeStatus = (status: string) => {
    setPositionStatusFilter(status);
  };

  const handleAssignationClick = (externalId: string) => {
    setAssignationExternalId(externalId);
  };

  const handleCloseAssignationModal = (assignationUpdated: boolean) => {
    onAssignationUpdate(assignationUpdated);
    setAssignationExternalId(undefined);
  };
  const handlePositionClick = (externalId: string) => {
    setEditMode(true);
    setPositionExternalId(externalId);
  };

  const handleClosePositionModal = (action: "update" | "create" | "canceled") => {
    setPositionExternalId(undefined);
    setEditMode(true);
    if (action === "update" || action === "create") {
      fetchPositions();
      return;
    }
  };

  const handleOnClickCreate = async () => {
    const names = await loadUserNames();
    if (!names) return;
    setUserNames(names);
    setEditMode(false);
    setPositionExternalId(undefined);
  };

  return (
    <AppContainerFullHeight
      bottomSectionRef={bottomSectionRef}
      topSection={
        <AppToolbar title={t("Project Timeline")}>
          <ProjectFilters
            projectsNames={projectsNames}
            selectedProject={selectedProject}
            onChangeProject={handleSelectProject}
            positionStatus={positionStatusFilter}
            onChangePositionStatus={onChangeStatus}
            canCreate={selectedProject && userIsManager}
            onClickCreate={handleOnClickCreate}
          />
        </AppToolbar>
      }
      bottomSection={
        <SpinnerWrapper isLoading={isLoading}>
          <TimelineComponent
            groups={groups}
            items={items}
            start={startDate}
            updateStartTime={setStartDate}
            onItemClick={handleAssignationClick}
            onGroupClick={userIsManager ? handlePositionClick : undefined}
          />
          {assignationExternalId && <AssignationEditDialog externalId={assignationExternalId} onClose={handleCloseAssignationModal} />}
          {userIsManager && (!editMode || positionExternalId) && (
            <PositionFormDialog
              externalId={positionExternalId}
              onClose={handleClosePositionModal}
              editMode={editMode}
              userNames={userNames}
              projectExternalId={selectedProject!.externalId}
            />
          )}
        </SpinnerWrapper>
      }
    />
  );
}

export default ProjectTimelineView;
